import { useCallback, useEffect, useMemo, useState } from 'react';

import useKycPendingStatus from '../hooks/useKycPendingStatus';
import usePlanExpiryToast from '../hooks/usePlanStatus';
import KycPendingBannerContent from './KycPendingBanner';
import PlanExpiredToastContent from './PlanExpiredToast';

const ToastCarousel = () => {
  const { isKycPending } = useKycPendingStatus();
  const { planExpiryMessage, shouldShowNextPaymentAlert } =
    usePlanExpiryToast();

  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const toastItems = useMemo(
    () => [
      {
        id: 'kyc-pending',
        content: <KycPendingBannerContent />,
        isVisible: isKycPending
      },
      {
        id: 'plan-expired',
        content: (
          <PlanExpiredToastContent
            message={planExpiryMessage}
            className={isKycPending ? '!pb-12' : ''}
          />
        ),
        isVisible: shouldShowNextPaymentAlert
      }
    ],
    [isKycPending, planExpiryMessage, shouldShowNextPaymentAlert]
  );

  const visibleItems = toastItems.filter((item) => item.isVisible);

  const handleNext = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % visibleItems.length);
      setIsTransitioning(false);
    }, 300);
  }, [visibleItems.length]);

  const handleDotClick = useCallback(
    (index: number) => {
      if (index === activeIndex) return;
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveIndex(index);
        setIsTransitioning(false);
      }, 300);
    },
    [activeIndex]
  );

  useEffect(() => {
    if (visibleItems.length <= 1) return undefined;

    const interval = setInterval(handleNext, 12000);
    return () => {
      clearInterval(interval);
    };
  }, [visibleItems.length, handleNext]);

  useEffect(() => {
    if (activeIndex >= visibleItems.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, visibleItems.length]);

  if (visibleItems.length === 0) return null;

  if (visibleItems.length === 1) {
    return <div>{visibleItems[0].content}</div>;
  }

  return (
    <div className="relative mt-4 rounded-xl bg-yellowLite1">
      <div className="overflow-hidden">
        <div
          className="transition-all duration-500 ease-in-out"
          style={{
            opacity: isTransitioning ? 0 : 1,
            transform: isTransitioning ? 'translateX(-10px)' : 'translateX(0)',
            transition: 'opacity 300ms ease-in-out, transform 300ms ease-in-out'
          }}
        >
          {visibleItems[activeIndex]?.content}
        </div>
      </div>
      <div className=" absolute bottom-2 left-1/2 flex w-full -translate-x-1/2 items-center justify-center gap-2">
        {visibleItems.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`Go to slide ${index + 1}`}
            onClick={() => handleDotClick(index)}
            className={`size-1.5 rounded-full transition-all duration-300 ${
              index === activeIndex
                ? 'w-5 bg-cofeeYellow6'
                : 'bg-cofeeGreyBlue200'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default ToastCarousel;
